
import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowUpRight,
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Minus,
  Plus,
  Ruler,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { BASE_URL } from "../../../config/config";
import axios from "axios";

/* ---------------- Helpers ---------------- */

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") {
    return "AED 0";
  }

  return new Intl.NumberFormat("en-AE", {
    style: "currency",
    currency: "AED",
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "0";
  }

  return new Intl.NumberFormat("en-AE").format(Number(value));
};

/* ---------------- Main Component ---------------- */

const ViewProperty = ({ onBack, onBuy }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [shares, setShares] = useState(1);

  const navigate = useNavigate();
  const location = useLocation();

  const ID = location.state?.id;

  /* ---------------- API ---------------- */

  const viewDetails = async () => {
    if (!ID) return;

    try {
      setIsLoading(true);

      const token = localStorage.getItem("rwatoken");

      const response = await axios.get(
        `${BASE_URL}/owner/me/properties/${ID}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );


      if (response?.data) {
        setSelectedProperty(response.data);
        setSelectedImage(0);
      }
    } catch (error) {
      console.log("Error fetching property details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    viewDetails();
  }, [ID]);

  /* ---------------- Property Mapping ---------------- */

  const property = useMemo(() => {
    if (!selectedProperty) {
      return null;
    }

    const totalBlocks = Number(selectedProperty?.total_tokens || 0);
    const blocksSold = Number(selectedProperty?.blocks_sold || 0);

    const availableBlocks = Math.max(totalBlocks - blocksSold, 0);

    /*
      Convert API image paths into complete URLs.

      API:
      property_images/45d005ace67d45949a6537dc7e4a024e.png

      Final:
      http://18.145.218.203:8000/uploads/property_images/...
    */
    const images =
      selectedProperty?.image_paths?.length > 0
        ? selectedProperty.image_paths.map(
            (image) => `${BASE_URL}/uploads/${image}`
          )
        : [];

    return {
      id: selectedProperty?.id,

      name: selectedProperty?.name,

      description: selectedProperty?.description,

      location: selectedProperty?.location,

      type: selectedProperty?.property_type,

      category: selectedProperty?.property_type,

      images,

      propertyValue: Number(
        selectedProperty?.property_value_aed || 0
      ),

      sharePrice: Number(
        selectedProperty?.price_per_block_aed || 0
      ),

      totalShares: totalBlocks,

      availableShares: availableBlocks,

      blocksSold: blocksSold,

      roi: Number(
        selectedProperty?.gross_annual_yield_pct || 0
      ),

      area: selectedProperty?.area_sqft,

      fundingPercentage: Number(
        selectedProperty?.funding_pct || 0
      ),

      status: selectedProperty?.is_published
        ? "PUBLISHED"
        : "UNPUBLISHED",

      submissionStatus:
        selectedProperty?.submission_status,

      rejectionReason:
        selectedProperty?.rejection_reason,

      tokenAddress:
        selectedProperty?.token_address,
    };
  }, [selectedProperty]);

  /* ---------------- Images ---------------- */

  const images = property?.images?.length
    ? property.images
    : [];

  /* ---------------- Calculations ---------------- */

  const totalInvestment = useMemo(() => {
    return shares * Number(property?.sharePrice || 0);
  }, [shares, property?.sharePrice]);

  const fundedPercentage = useMemo(() => {
    if (!property?.totalShares) {
      return 0;
    }

    /*
      Prefer API funding_pct when available.
      Otherwise calculate from blocks sold.
    */
    if (
      selectedProperty?.funding_pct !== null &&
      selectedProperty?.funding_pct !== undefined
    ) {
      return Number(selectedProperty.funding_pct);
    }

    return (
      (Number(property?.blocksSold || 0) /
        Number(property?.totalShares || 1)) *
      100
    );
  }, [
    property?.totalShares,
    property?.blocksSold,
    selectedProperty?.funding_pct,
  ]);

  /*
    Ownership percentage for one token/block.

    Example:
    10,000 total blocks
    1 block = 0.01%
  */
  const ownershipPerShare = useMemo(() => {
    if (!property?.totalShares) {
      return "0%";
    }

    const percentage = 100 / Number(property.totalShares);

    return `${percentage.toFixed(2)}%`;
  }, [property?.totalShares]);

  /* ---------------- Share Controls ---------------- */

  const increaseShares = () => {
    if (shares < Number(property?.availableShares || 0)) {
      setShares((prev) => prev + 1);
    }
  };

  const decreaseShares = () => {
    if (shares > 1) {
      setShares((prev) => prev - 1);
    }
  };

  /* ---------------- Loading ---------------- */

  if (isLoading) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#fdfbf5] font-['Inter']">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -left-28 top-[-80px] h-[420px] w-[420px] rounded-full bg-[#dfe8cf]/70 blur-[110px]" />
          <div className="absolute right-[-150px] top-[18%] h-[500px] w-[500px] rounded-full bg-[#e7ecd9]/80 blur-[125px]" />
          <div className="absolute bottom-[-220px] left-[28%] h-[520px] w-[520px] rounded-full bg-[#eef1e4]/90 blur-[125px]" />
          <div className="absolute inset-0 bg-white/25 backdrop-blur-[4px]" />
        </div>

        <div className="relative z-10 w-[min(92%,420px)] rounded-[28px] border border-white/80 bg-white/75 p-8 text-center shadow-[0_30px_80px_rgba(55,92,32,0.14)] backdrop-blur-2xl">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[20px] bg-gradient-to-br from-[#4c7a2e] to-[#6fa142] text-white shadow-[0_12px_28px_rgba(55,92,32,0.22)]">
            <Building2 size={28} strokeWidth={1.7} />
          </div>

          <div className="relative mx-auto mt-6 h-11 w-11">
            <div className="absolute inset-0 rounded-full border-[3px] border-[#dfe5d5]" />
            <div className="absolute inset-0 animate-spin rounded-full border-[3px] border-transparent border-t-[#4c7a2e] border-r-[#9eae7b]" />
          </div>

          <p className="mt-5 text-[18px] font-extrabold tracking-[-0.025em] text-[#1f2318]">
            Loading property details
          </p>

          <p className="mx-auto mt-2 max-w-[280px] text-[12px] font-medium leading-6 text-[#68705b]">
            Preparing the property information and investment details for you.
          </p>

          <div className="mt-5 flex items-center justify-center gap-1.5" aria-hidden="true">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#4c7a2e]" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#8a9463] [animation-delay:150ms]" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#b7c29a] [animation-delay:300ms]" />
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#fdfbf5] font-['Inter']">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -left-32 top-[-100px] h-[430px] w-[430px] rounded-full bg-[#e2ead5]/80 blur-[110px]" />
          <div className="absolute right-[-150px] bottom-[-160px] h-[480px] w-[480px] rounded-full bg-[#e7ecd9]/80 blur-[120px]" />
        </div>
        <div className="relative z-10 rounded-[26px] border border-white/80 bg-white/75 px-10 py-9 text-center shadow-[0_25px_70px_rgba(55,92,32,0.12)] backdrop-blur-2xl">
          <Building2
            size={35}
            className="mx-auto text-[#4c7a2e]"
          />

          <h2 className="mt-4 text-[22px] font-extrabold tracking-[-0.025em] text-[#1f2318]">
            Property not found
          </h2>

          <p className="mt-2 text-[12px] font-medium text-[#68705b]">
            We could not load the property details.
          </p>

          <button
            onClick={() => navigate(-1)}
            className="mt-6 flex mx-auto items-center gap-2 rounded-full bg-[#4c7a2e] px-5 py-3 text-[12px] font-bold text-white shadow-[0_10px_24px_rgba(55,92,32,0.18)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#123f56]"
          >
            <ArrowLeft size={13} />
            Back 
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#fdfbf5] font-['Inter'] text-[#292d22]">
      {/* Soft blurred background */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <div className="absolute -left-40 top-[-120px] h-[560px] w-[560px] rounded-full bg-[#dfe8cf]/70 blur-[125px]" />
        <div className="absolute right-[-180px] top-[16%] h-[620px] w-[620px] rounded-full bg-[#e7ecd9]/80 blur-[135px]" />
        <div className="absolute bottom-[-260px] left-[25%] h-[650px] w-[650px] rounded-full bg-[#e4f1f6]/90 blur-[145px]" />
        <div className="absolute left-[45%] top-[38%] h-[300px] w-[300px] rounded-full bg-white/60 blur-[100px]" />
      </div>

      {/* ================= HEADER ================= */}

      <header className="sticky top-0 z-30 border-b border-white/80 bg-white/75 shadow-[0_8px_30px_rgba(55,92,32,0.06)] backdrop-blur-2xl">
        <div className="mx-auto flex h-[76px] max-w-[1380px] items-center justify-between px-5 sm:px-7 lg:px-10">

          {/* Logo */}

          <div className="flex items-center gap-2.5">
            <div className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-gradient-to-br from-[#4c7a2e] to-[#6fa142] text-white shadow-[0_10px_24px_rgba(55,92,32,0.18)]">
              <Building2 size={18} strokeWidth={1.7} />
            </div>

            <div>
              <div className="font-['Playfair_Display'] text-[21px] font-semibold leading-none tracking-[-0.02em] text-[#1f2318]">
                Property
              </div>
            </div>
          </div>

          {/* Back */}

          <button
            onClick={() => navigate(-1)}
            className="group flex items-center gap-2 rounded-full border border-white/90 bg-white/75 px-4 py-2.5 text-[12px] font-bold text-[#59614c] shadow-[0_7px_20px_rgba(55,92,32,0.07)] backdrop-blur-xl transition-all duration-300 hover:-translate-x-0.5 hover:bg-white hover:text-[#4c7a2e] hover:shadow-[0_12px_28px_rgba(55,92,32,0.11)]"
          >
            <ArrowLeft size={13} />
            Back
          </button>
        </div>
      </header>

      {/* ================= MAIN ================= */}

      <main className="relative z-10 mx-auto max-w-[1380px] px-5 py-8 sm:px-7 lg:px-10 sm:py-10">

        {/* Breadcrumb */}

        <div className="mb-6 flex items-center gap-2 text-[11px] font-medium text-[#858b73]">
          <span>Investments</span>

          <span>/</span>

          <span className="font-bold text-[#4c7a2e]">
            {property?.type}
          </span>
        </div>

        {/* Main Grid */}

        <div className="grid grid-cols-1 gap-7 lg:grid-cols-[1.55fr_1fr] lg:gap-8">

          {/* ================= LEFT ================= */}

          <div>

            {/* Image */}

            <div className="relative overflow-hidden rounded-[24px] border border-white/90 bg-white/70 shadow-[0_20px_55px_rgba(55,92,32,0.10)] backdrop-blur-xl">

              <div className="relative h-[340px] sm:h-[460px] lg:h-[500px]">

                {images.length > 0 ? (
                  <img
                    src={images[selectedImage]}
                    alt={property?.name || "Property"}
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.015]"
                    onError={(e) => {
                      console.log(
                        "Image failed to load:",
                        e.currentTarget.src
                      );
                    }}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center bg-[#f0f0e8]">
                    <Building2
                      size={45}
                      strokeWidth={1}
                      className="text-[#969b8b]"
                    />
                  </div>
                )}

                {/* Gradient */}

                <div className="absolute inset-0 bg-gradient-to-t from-[#1d2417]/55 via-transparent to-transparent" />

                {/* Category */}

                <div className="absolute left-4 top-4">
                  <span className="rounded-full border border-white/70 bg-white/90 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-[#4c7a2e] shadow-sm backdrop-blur-md">
                    {property?.category}
                  </span>
                </div>

                {/* Status */}

                <div className="absolute right-4 top-4">
                  <span
                    className={`rounded-[6px] px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-white ${
                      property?.status === "PUBLISHED"
                        ? "bg-[#4f7a38]"
                        : "bg-[#375c20]"
                    }`}
                  >
                    {property?.status}
                  </span>
                </div>

                {/* Image navigation */}

                {images.length > 1 && (
                  <>
                    <button
                      onClick={() =>
                        setSelectedImage(
                          selectedImage === 0
                            ? images.length - 1
                            : selectedImage - 1
                        )
                      }
                      className="absolute left-4 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-white/80 bg-white/90 text-[#4c7a2e] shadow-[0_8px_20px_rgba(55,92,32,0.12)] backdrop-blur-md transition-all hover:scale-105"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    <button
                      onClick={() =>
                        setSelectedImage(
                          selectedImage === images.length - 1
                            ? 0
                            : selectedImage + 1
                        )
                      }
                      className="absolute right-4 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-white/80 bg-white/90 text-[#4c7a2e] shadow-[0_8px_20px_rgba(55,92,32,0.12)] backdrop-blur-md transition-all hover:scale-105"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnails */}

              {images.length > 1 && (
                <div className="flex gap-3 overflow-x-auto bg-white/60 p-4 backdrop-blur-xl">
                  {images.map((image, index) => (
                    <button
                      key={`${image}-${index}`}
                      onClick={() =>
                        setSelectedImage(index)
                      }
                      className={`h-[64px] w-[84px] shrink-0 overflow-hidden rounded-[10px] border-2 transition-all duration-300 ${
                        selectedImage === index
                          ? "border-[#4c7a2e] shadow-[0_6px_16px_rgba(76,122,46,0.18)]"
                          : "border-transparent"
                      }`}
                    >
                      <img
                        src={image}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ================= HEADING ================= */}

            <div className="mt-7 rounded-[22px] border border-white/80 bg-white/55 p-6 shadow-[0_14px_35px_rgba(55,92,32,0.06)] backdrop-blur-xl">

              <div className="flex items-center gap-2 text-[12px] font-semibold text-[#68705b]">
                <MapPin size={11} />
                {property?.location}
              </div>

              <h1 className="mt-2 font-['Playfair_Display'] text-[32px] font-semibold leading-tight tracking-[-0.025em] text-[#1f2318] sm:text-[40px]">
                {property?.name}
              </h1>

              <p className="mt-2 text-[13px] font-semibold uppercase tracking-[0.08em] text-[#777e68]">
                {property?.type}
              </p>

              <p className="mt-4 max-w-[820px] text-[14px] font-medium leading-7 text-[#606650]">
                {property?.description}
              </p>
            </div>

            {/* ================= PROPERTY INFORMATION ================= */}

            <section className="mt-6 rounded-[24px] border border-white/90 bg-white/70 p-6 shadow-[0_16px_42px_rgba(55,92,32,0.07)] backdrop-blur-xl sm:p-7">

              <div className="mb-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4c7a2e]">
                  Property Information
                </p>

                <h2 className="mt-1.5 font-['Playfair_Display'] text-[24px] font-semibold text-[#1f2318]">
                  About this property
                </h2>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">

                {/* Property Type */}

                <InfoItem
                  icon={<Building2 size={14} />}
                  label="Property Type"
                  value={property?.type}
                />

                {/* Area */}

                <InfoItem
                  icon={<Ruler size={14} />}
                  label="Area"
                  value={
                    property?.area
                      ? `${formatNumber(property.area)} sq.ft`
                      : "-"
                  }
                />

                {/* Total Tokens */}

                <InfoItem
                  icon={<Building2 size={14} />}
                  label="Total Tokens"
                  value={formatNumber(property?.totalShares)}
                />

                {/* Tokens Sold */}

                <InfoItem
                  icon={<CheckCircle2 size={14} />}
                  label="Tokens Sold"
                  value={formatNumber(property?.blocksSold)}
                />

                {/* Available */}

                <InfoItem
                  icon={<CheckCircle2 size={14} />}
                  label="Available Tokens"
                  value={formatNumber(property?.availableShares)}
                />

                {/* Submission */}

                <InfoItem
                  icon={<ShieldCheck size={14} />}
                  label="Submission Status"
                  value={
                    property?.submissionStatus
                      ?.replaceAll("_", " ")
                      ?.toUpperCase() || "-"
                  }
                />

                {/* Yield */}

                <InfoItem
                  icon={<TrendingUp size={14} />}
                  label="Annual Yield"
                  value={`${property?.roi || 0}%`}
                />

                {/* Ownership */}

                <InfoItem
                  icon={<Building2 size={14} />}
                  label="Ownership / Token"
                  value={ownershipPerShare}
                />
              </div>
            </section>

            {/* ================= FINANCIALS ================= */}

           
          </div>

          {/* ================= RIGHT ================= */}

          <div className="lg:self-start gap-4 flex flex-col">

            {/* Investment Card */}

            <div className="overflow-hidden rounded-[26px] border border-white/90 bg-white/75 shadow-[0_24px_60px_rgba(55,92,32,0.12)] backdrop-blur-2xl">

              {/* Total value */}

              <div className="border-b border-[#e5e8dc] bg-white/40 px-6 py-6">

                <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#777e68]">
                  Total Property Value
                </p>

                <div className="mt-1 flex items-end justify-between">

                  <h2 className="text-[30px] font-extrabold tracking-[-0.025em] text-[#1f2318]">
                    {formatCurrency(
                      property?.propertyValue
                    )}
                  </h2>

                  <div className="flex items-center gap-1 rounded-full border border-[#d9e6c8] bg-[#edf4e3] px-3 py-1.5 text-[12px] font-bold text-[#4c7a2e]">
                    <TrendingUp size={10} />

                    {property?.roi || 0}% ROI
                  </div>
                </div>
              </div>

              {/* Investment metrics */}

              <div className="grid grid-cols-3 border-b border-[#e5e8dc] bg-[#faf9f2]/70">

                <Metric
                  value={formatNumber(
                    property?.totalShares
                  )}
                  label="Total tokens"
                />

                <Metric
                  value={formatCurrency(
                    property?.sharePrice
                  )}
                  label="Price / token"
                />

                <Metric
                  value={ownershipPerShare}
                  label="Ownership"
                />
              </div>

              {/* Availability */}

              <div className="px-6 py-5">

                <div className="flex items-center justify-between">

                  <p className="text-[11px] font-semibold text-[#68705b]">
                    Investment availability
                  </p>

                  <p className="text-[11px] font-bold text-[#4c7a2e]">
                    {formatNumber(
                      property?.availableShares
                    )}{" "}
                    tokens left
                  </p>
                </div>

                <div className="mt-3 h-[8px] overflow-hidden rounded-full bg-[#e3e7d8]">

                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#4c7a2e] to-[#6fa142] transition-all duration-700"
                    style={{
                      width: `${Math.min(
                        Math.max(fundedPercentage, 0),
                        100
                      )}%`,
                    }}
                  />
                </div>

                <div className="mt-2 flex justify-between text-[10px] font-medium text-[#7b826c]">

                  <span>
                    {Math.round(
                      fundedPercentage
                    )}
                    % funded
                  </span>

                  <span>
                    {formatNumber(
                      property?.totalShares
                    )}{" "}
                    total tokens
                  </span>
                </div>
              </div>

              {/* Buy section */}

              <div className="border-t border-[#e5e8dc] bg-[#f6f5eb]/75 px-6 py-6">

                <div className="mb-4">

                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#4c7a2e]">
                    Your Investment
                  </p>

                  <p className="mt-1.5 text-[12px] font-medium leading-6 text-[#68705b]">
                    Purchase fractional ownership
                    instead of buying the entire
                    property.
                  </p>
                </div>

                {/* Shares selector */}

                {/* <div className="flex items-center justify-between rounded-[8px] border border-[#e1e3e1] bg-white p-2.5">

                  <div>

                    <p className="text-[9px] font-medium text-[#788286]">
                      Number of tokens
                    </p>

                    <p className="mt-0.5 text-[12px] text-[#a0a5a6]">
                      {formatCurrency(
                        property?.sharePrice
                      )}{" "}
                      each
                    </p>
                  </div>

                  <div className="flex items-center gap-3">

                    <button
                      onClick={decreaseShares}
                      disabled={shares <= 1}
                      className="flex h-7 w-7 items-center justify-center rounded-[6px] border border-[#e1e3e1] text-[#375c20] transition hover:border-[#375c20] disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <Minus size={12} />
                    </button>

                    <span className="min-w-[25px] text-center text-[13px] font-bold text-[#292d22]">
                      {shares}
                    </span>

                    <button
                      onClick={increaseShares}
                      disabled={
                        shares >=
                        Number(
                          property?.availableShares || 0
                        )
                      }
                      className="flex h-7 w-7 items-center justify-center rounded-[6px] border border-[#e1e3e1] text-[#375c20] transition hover:border-[#375c20] disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                </div> */}

                {/* Total */}

                {/* <div className="mt-4 flex items-center justify-between">

                  <span className="text-[10px] font-medium text-[#737d80]">
                    Total investment
                  </span>

                  <span className="text-[21px] font-semibold text-[#292d22]">
                    {formatCurrency(
                      totalInvestment
                    )}
                  </span>
                </div> */}

                {/* Buy */}

                {/* <button
                  onClick={() =>
                    onBuy?.({
                      property: selectedProperty,
                      shares,
                      totalInvestment,
                    })
                  }
                  className="mt-4 flex h-[46px] w-full items-center justify-center gap-2 rounded-[8px] bg-[#375c20] text-[11px] font-semibold text-white shadow-[0_6px_16px_rgba(32,72,91,0.18)] transition hover:-translate-y-px hover:bg-[#173d4f]"
                >
                  Buy {shares}{" "}
                  {shares === 1
                    ? "Token"
                    : "Tokens"}

                  <ArrowUpRight size={14} />
                </button>

                <div className="mt-3 flex items-center justify-center gap-1.5 text-[8px] text-[#92999b]">
                  <ShieldCheck size={11} />
                  Secure fractional investment
                </div> */}
              </div>
            </div>
             <section className="mt-5 rounded-[24px] border border-white/90 bg-white/70 p-6 shadow-[0_16px_42px_rgba(55,92,32,0.07)] backdrop-blur-xl sm:p-7">

              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4c7a2e]">
                Financials
              </p>

              <h2 className="mt-1.5 font-['Playfair_Display'] text-[24px] font-semibold text-[#1f2318]">
                Investment breakdown
              </h2>

              <div className="mt-5 overflow-hidden rounded-[16px] border border-[#e2e6d9] bg-[#f9fcfd]/75">

                <FinancialRow
                  label="Property Value"
                  value={formatCurrency(
                    property?.propertyValue
                  )}
                />

                <FinancialRow
                  label="Price / Token"
                  value={formatCurrency(
                    property?.sharePrice
                  )}
                />

                <FinancialRow
                  label="Gross Annual Yield"
                  value={`${property?.roi || 0}%`}
                />

                <FinancialRow
                  label="Funding"
                  value={`${Number(
                    property?.fundingPercentage || 0
                  ).toFixed(1)}%`}
                />

                <FinancialRow
                  label="Tokens Sold"
                  value={formatNumber(
                    property?.blocksSold
                  )}
                />
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

/* ================= Components ================= */

const Metric = ({ value, label }) => {
  return (
    <div className="px-3 py-5 text-center transition-colors hover:bg-white/70">

      <p className="text-[17px] font-extrabold text-[#4c7a2e]">
        {value}
      </p>

      <p className="mt-1.5 text-[11px] font-medium text-[#7b826c]">
        {label}
      </p>
    </div>
  );
};

const InfoItem = ({ icon, label, value }) => {
  return (
    <div className="group flex items-center gap-3 rounded-[16px] border border-[#e1e8d6] bg-[#faf9f2]/85 p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#c9d6b5] hover:shadow-[0_10px_24px_rgba(55,92,32,0.08)]">

      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-[#e9efdf] text-[#4c7a2e] transition-transform duration-300 group-hover:scale-105">
        {icon}
      </div>

      <div>
        <p className="text-[10px] font-medium uppercase tracking-[0.06em] text-[#7b826c]">
          {label}
        </p>

        <p className="mt-1 text-[14px] font-bold text-[#3e472f]">
          {value}
        </p>
      </div>
    </div>
  );
};

const FinancialRow = ({ label, value }) => {
  return (
    <div className="flex items-center justify-between border-b border-[#e4e7db] px-5 py-4 last:border-b-0">

      <span className="text-[12px] font-medium text-[#68705b]">
        {label}
      </span>

      <span className="text-[14px] font-bold text-[#3e472f]">
        {value}
      </span>
    </div>
  );
};

export default ViewProperty;

